import {Component, computed, effect, inject, signal} from '@angular/core';
import {NgOptimizedImage} from '@angular/common';
import {ElciaButton} from '../../components/buttons/button/button';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {LocalDropdown} from '../../components/local-dropdown/local-dropdown';
import {TextInput} from '../../components/text-input/text-input';
import {AuthService, ForgotPasswordRequest, LoginRequest} from '../../../api';
import {lastValueFrom} from 'rxjs';
import {TokenService} from '../token.service';
import {ActivatedRoute, Router} from '@angular/router';
import {injectMutation} from '@tanstack/angular-query-experimental';
import {HttpErrorResponse} from '@angular/common/http';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {toSignal} from '@angular/core/rxjs-interop';
import {ElciaDialog} from '../../components/dialog/dialog';
import {ToastMessage, ToastService} from '../../services/toast.service';

@Component({
  selector: 'elcia-login',
  imports: [
    NgOptimizedImage,
    ElciaButton,
    TranslatePipe,
    LocalDropdown,
    TextInput,
    ReactiveFormsModule,
    ElciaDialog,
  ],
  templateUrl: './login.html',
  styleUrl: './login.scss',
})
export class Login {
  error = signal<string | null>(null);

  private readonly queryParams = toSignal(inject(ActivatedRoute).queryParamMap);


  email = computed(() => {
    return this.queryParams()?.get('email');
  });

  resetPasswordEmailControl = new FormControl('', [Validators.required, Validators.email]);

  fg = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email]),
    password: new FormControl('', Validators.required),
  });
  private onEmailChange = effect(() => {
    if (this.email() && !this.fg.controls.email.value) {
      this.fg.controls.email.setValue(this.email()!);
    }
  })
  private readonly authService = inject(AuthService);
  private readonly tokenService = inject(TokenService);
  private readonly toastService = inject(ToastService);
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);

  loginMutation = injectMutation(() => ({
    mutationFn: (request: LoginRequest) => lastValueFrom(this.authService.login(request)),
    onSuccess: (response) => {
      if (response.token && response.refreshToken) {
        this.tokenService.login(response.token, response.refreshToken);
        void this.router.navigate(['/']);
      }
    },
    onError: (error: HttpErrorResponse) => {
      if (error.status === 401) {
        this.error.set('app.invalid-credentials');
      }
    }
  }));

  protected isForgotPasswordDisplayed = signal(false);

  protected logIn(): void {
    if (this.fg.invalid) {
      this.fg.markAllAsTouched();
      return;
    }
    this.error.set(null);
    this.loginMutation.mutate({
      email: this.fg.controls.email.value!,
      password: this.fg.controls.password.value!,
    });
  }

  protected openForgotPasswordForm() {
    this.isForgotPasswordDisplayed.set(true);
  }

  protected sendResetPasswordEmail() {
    if (this.resetPasswordEmailControl.invalid) {
      this.resetPasswordEmailControl.markAllAsTouched();
      return;
    }

    const emailValue = this.resetPasswordEmailControl.value!;
    this.forgetPasswordMutation.mutate({email: emailValue});
  }

  forgetPasswordMutation = injectMutation(() => ({
    mutationFn: (request: ForgotPasswordRequest) => lastValueFrom(this.authService.forgotPassword(request)),
    onSuccess: () => {
      const message: ToastMessage = {
        detail: this.translateService.instant('app.reset-password-email-sent'),
        severity: this.translateService.instant('info'),
        summary: this.translateService.instant('app.email-sent')
      } as ToastMessage;
      this.toastService.showMessage(message);
      this.closeForgotPasswordForm();
    }
  }))

  protected closeForgotPasswordForm() {
    this.isForgotPasswordDisplayed.set(false);
  }
}
